import { useState } from 'react'

import { Option } from '@joor/design-system'
import { FormikErrors, FormikProps, useFormik } from 'formik'
import { useSelector } from 'react-redux'
import * as Yup from 'yup'

import { BrandDoor, ShippingAddress } from '__generated__/atlas-types'

import { formatNameWithCode } from 'utils/formatters'

import { isAccountTypeBrand as isAccountTypeBrandSelector } from 'store/currentUser/selectors'

import {
  EMPTY_FORM_STATE,
  FieldIds,
  ShippingAddressFormValues,
} from '../../Forms/ShippingAddressForm/shippingAddressForm.types'
import {
  formatOrderShippingToFormValues,
  formatRetailerLocationToFormValues,
  formatShippingAddressToFormValues,
  getInitialShippingFormValues,
} from '../../Forms/ShippingAddressForm/shippingAddressForm.utils'
import {
  DEFAULT_ORDER_ADDRESS_ID,
  REQUIRED_FIELD_ERROR_MESSAGE,
  extractCompanyFromLabel,
  getCompanyNameLabel,
} from '../../Forms/forms.utils'
import { RetailerInfo } from './useAddressModalData'
import { OrderBillingShippingData } from 'features/Orders/Review/components/Overview/components/AddressesWidget/addressesWidget.queries'
import { messages } from 'features/Orders/Review/components/Overview/components/AddressesWidget/constants/messages'
import { isSameShippingAddressThanValues } from 'features/Orders/Review/utils/addresses.utils'
import { getDropdownCallbacks } from 'features/Orders/Review/utils/formik.utils'

const phoneRegExp = new RegExp(/^\+?[\d\s\-()]*$/)

const ValidationSchema = Yup.object().shape({
  [FieldIds.AddressOption]: Yup.object()
    .required(REQUIRED_FIELD_ERROR_MESSAGE)
    .nullable(),
  [FieldIds.CompanyName]: Yup.string().required(REQUIRED_FIELD_ERROR_MESSAGE),
  [FieldIds.Email]: Yup.array()
    .transform(function (value, originalValue) {
      if (this.isType(value) && value !== null) {
        return value
      }
      return originalValue ? originalValue.split(/[\s,]+/) : []
    })
    .of(Yup.string().email(({ value }) => `${value} is not a valid email`))
    .required(REQUIRED_FIELD_ERROR_MESSAGE),
  [FieldIds.Address1]: Yup.string().required(REQUIRED_FIELD_ERROR_MESSAGE),
  [FieldIds.City]: Yup.string().required(REQUIRED_FIELD_ERROR_MESSAGE),
  [FieldIds.Country]: Yup.object()
    .required(REQUIRED_FIELD_ERROR_MESSAGE)
    .nullable(),
  [FieldIds.Phone]: Yup.string().matches(phoneRegExp, 'Not valid phone number'),
  [FieldIds.Territory]: Yup.string().max(
    40,
    'Input cannot exceed 40 characters',
  ),
})

type UseShippingFormParams = {
  orderDoor?: BrandDoor
  orderShipping?: OrderBillingShippingData['shipping']
  trackingNumber?: string | null
  orderShippingMethodId?: string
  countryOptions?: Option[]
  doorOptions?: Option[]
  shippingMethodOptions?: Option[]
  shippingAddresses?: ShippingAddress[]
  aggregatedOptions?: Option[]
  retailerInfo?: RetailerInfo
}

export type ShippingFormikErrors = FormikErrors<ShippingAddressFormValues>
export type HandleShippingChange =
  FormikProps<ShippingAddressFormValues>['handleChange']

export function useShippingForm({
  orderDoor,
  orderShipping,
  trackingNumber,
  orderShippingMethodId,
  countryOptions = [],
  doorOptions = [],
  shippingAddresses = [],
  shippingMethodOptions = [],
  aggregatedOptions = [],
  retailerInfo,
}: UseShippingFormParams) {
  const isAccountTypeBrand = useSelector(isAccountTypeBrandSelector)
  const [disableCompanyField, setDisableCompanyField] =
    useState(!isAccountTypeBrand)
  const [csdSelectedAddress, setCsdSelectedAddress] =
    useState<ShippingAddress>()
  const [isPrimaryLocationSet, setIsPrimaryLocationSet] = useState(false)
  const [storeNameLabel, setStoreNameLabel] = useState<string>(
    messages.storeName,
  )

  const getCountryOption = (countryName?: string | null) =>
    countryOptions.find((country) => country.label === countryName)

  // this is needed due to when the user is a retailer
  // the user has not access to the door options
  let doorDefaultValue = null
  if (isAccountTypeBrand) {
    doorDefaultValue =
      doorOptions.find(({ value }) => value === orderDoor?.id) ?? null
  } else if (orderDoor) {
    doorDefaultValue = {
      value: orderDoor.id,
      label: formatNameWithCode(orderDoor.name, orderDoor.code),
    }
  }

  const initialValues: ShippingAddressFormValues = {
    ...getInitialShippingFormValues({
      orderShipping,
      countryOptions,
      shippingAddressOptions: aggregatedOptions,
    }),
    door: doorDefaultValue,
    trackingNumber: trackingNumber ? trackingNumber.trim() : '',
    shippingMethod:
      shippingMethodOptions.find(
        ({ value }) => value === orderShippingMethodId,
      ) ?? null,
  }

  const formik = useFormik<ShippingAddressFormValues>({
    onSubmit: () => {},
    initialValues,
    validationSchema: ValidationSchema,
  })

  const handleSelectAddress = ({ selected }: { selected: Option }) => {
    const isSelectedPrimaryLocation =
      selected.value === retailerInfo?.primaryLocation?.id
    setIsPrimaryLocationSet(isSelectedPrimaryLocation)
    setCsdSelectedAddress(undefined)

    const isSelectedRetailerLocation =
      retailerInfo?.locations?.some(
        (location) => location.id === selected.value,
      ) && !isSelectedPrimaryLocation

    const isSelectedOrderShippingAddress =
      selected.value === DEFAULT_ORDER_ADDRESS_ID

    const commonValues = {
      selected: { ...selected, label: extractCompanyFromLabel(selected.label) },
      trackingNumber: formik.values[FieldIds.TrackingNumber],
      shippingMethodOption: formik.values[FieldIds.ShippingMethod],
      doorOption: formik.values[FieldIds.Door],
    }

    if (isSelectedPrimaryLocation) {
      formik.setValues(
        formatRetailerLocationToFormValues({
          ...commonValues,
          retailerLocation: retailerInfo?.primaryLocation,
          countryOption: getCountryOption(
            retailerInfo?.primaryLocation?.country,
          ),
          email: retailerInfo?.email,
        }),
      )
      setStoreNameLabel(
        getCompanyNameLabel(retailerInfo?.primaryLocation?.type),
      )
      setDisableCompanyField(isAccountTypeBrand)
      return
    }

    if (isSelectedRetailerLocation) {
      const foundRetailerLocation = retailerInfo?.locations?.find(
        (location) => location.id === selected.value,
      )
      if (foundRetailerLocation) {
        formik.setValues(
          formatRetailerLocationToFormValues({
            ...commonValues,
            retailerLocation: foundRetailerLocation,
            countryOption: getCountryOption(foundRetailerLocation?.country),
            email: retailerInfo?.email,
          }),
        )
        setStoreNameLabel(getCompanyNameLabel(foundRetailerLocation?.type))
      } else {
        // I can't imagine when this could happen, but just in case
        formik.setValues({
          ...EMPTY_FORM_STATE,
          [FieldIds.AddressOption]: selected,
        })
      }
      setDisableCompanyField(false)
      return
    }

    setStoreNameLabel(messages.storeName)
    setDisableCompanyField(!isAccountTypeBrand)

    if (isSelectedOrderShippingAddress) {
      formik.setValues(
        formatOrderShippingToFormValues({
          ...commonValues,
          orderShipping,
          countryOption: getCountryOption(orderShipping?.address.country),
        }),
      )
      return
    }

    const shippingAddress = shippingAddresses.find(
      (address) => address.id === selected.value,
    )

    if (isAccountTypeBrand && !!shippingAddress) {
      setCsdSelectedAddress(shippingAddress)
    }

    formik.setValues(
      formatShippingAddressToFormValues({
        ...commonValues,
        shippingAddress,
        countryOption: getCountryOption(shippingAddress?.address?.country),
      }),
    )
  }

  const countryDropdownHandlers = getDropdownCallbacks(FieldIds.Country, formik)

  const doorDropdownHandlers = getDropdownCallbacks(FieldIds.Door, formik)

  const shippingMethodDropdownHandlers = getDropdownCallbacks(
    FieldIds.ShippingMethod,
    formik,
    false,
  )

  const resetShippingForm = async () => {
    const values = {
      ...EMPTY_FORM_STATE,
      addressOption: formik.values[FieldIds.AddressOption],
    }
    formik.resetForm({
      values,
    })
    // we need to revalidate the form after reset
    // so the UI reflects the correct state
    await formik.validateForm(values)
  }

  const hasShippingCSDMismatch =
    !!csdSelectedAddress &&
    !isSameShippingAddressThanValues(csdSelectedAddress, formik.values)

  return {
    shippingFormik: formik,
    shippingValues: formik.values,
    shippingErrors: formik.errors,
    isValidShippingForm:
      formik.isValid && Boolean(formik.values[FieldIds.AddressOption]),
    isShippingFormTouched: formik.dirty,
    disableShippingCompanyField: disableCompanyField,
    shippingDoorHandlers: doorDropdownHandlers,
    shippingCountryHandlers: countryDropdownHandlers,
    shippingMethodHandlers: shippingMethodDropdownHandlers,
    handleSelectShippingAddress: handleSelectAddress,
    setShippingValues: formik.setValues,
    resetShippingForm,
    handleShippingChange: formik.handleChange,
    selectedCSDShippingAddress: csdSelectedAddress,
    isPrimaryLocShippingSet: isPrimaryLocationSet,
    hasShippingCSDMismatch,
    shippingNameLabel: storeNameLabel,
  }
}
